import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import {Tab, Tabs} from '@jetbrains/ring-ui/components/tabs/tabs'
import * as React from 'react'
import {useMemo} from 'react'
import type {PreloadedQuery, EntryPointProps} from 'react-relay'
import {usePreloadedQuery, useQueryLoader} from 'react-relay'
import {useLocation, useNavigate, useParams} from 'react-router-dom'

import {useQueryParam} from '../../../../hooks/routes'
import useDocumentTitle from '../../../../hooks/useDocumentTitle'
import useUpdateLastPage from '../../../../hooks/useUpdateLastPage'
import {PlaceIdList} from '../../../../plugins/plugins.types'
import HTTPError, {HTTPCodesEnum} from '../../../../rest/errors/HTTPError'
import {getAgentPoolHref, getAgentsOverviewHref, getHrefWithQueryParams} from '../../../../routes'
import type {AgentPoolId} from '../../../../types'
import {
  OverviewAgentPoolTabNamesEnum,
  OverviewAgentPoolTabNamesEnum as TabNames,
  toAgentPoolId,
  toTabId,
} from '../../../../types'
import {internalProps} from '../../../../types/BS_types'
import {LAST_AGENTS_PAGE_KEY} from '../../../../utils/lastPage'
import {subscribeOnOverallEvents} from '../../../../utils/subscriber'
import {AvailabilityErrorPage} from '../../../common/AvailabilityErrorPage/AvailabilityErrorPage'
import {getAvailabilityErrorMessage} from '../../../common/AvailabilityErrorPage/AvailabilityErrorPage.utils'
import ErrorBoundary from '../../../common/ErrorBoundary/ErrorBoundary'
import ErrorPage from '../../../common/ErrorPage/ErrorPage'
import Loader from '../../../common/Loader/Loader'
import {renderTitle} from '../../../common/OverviewTab/OverviewTab'
import Plugin from '../../../common/Plugin/Plugin'
import {AgentPagePoolsSubscriber, useAgentPools} from '../AgentPagePoolsProvider'
import {AgentPoolFavoriteStar} from '../AgentPoolsFavoritePage/Star/AgentPoolFavoriteStar'
import {OVERALL_AGENT_POOL_EVENT_TOPICS} from '../AgentsPages.actions'
import type {AgentsPagesQueries} from '../AgentsPages.queries'
import {getAgentPoolsTree} from '../AgentsPages.selectors'
import {AgentTreeItemType} from '../AgentsSidebar/AgentsSidebar.types'

import type {
  AgentPoolPagePoolQuery,
  AgentPoolPagePoolQuery$data,
} from './__generated__/AgentPoolPagePoolQuery.graphql'
import {PROJECT_AGENT_POOL_TYPENAME, STRING_DEFAULT_AGENT_POOL_ID} from './AgentPoolPage.constants'
import {agentPoolQuery} from './AgentPoolPage.queries'
import AgentsTab from './AgentsTab/AgentsTab'
import EditAgentPoolButton from './EditAgentPoolButton/EditAgentPoolButton'
import EditInClassicUILink from './EditInClassicUILink/EditInClassicUILink'
import ProjectsTab from './ProjectsTab/ProjectsTab'
import ProjectsTabTitle from './ProjectsTabTitle/ProjectsTabTitle'

import styles from './AgentPoolPage.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  agentPoolRawId: AgentPoolId
  agentPoolQueryRef: PreloadedQuery<AgentPoolPagePoolQuery>
  onUpdate?: () => void
}

const loader = <Loader />

function AgentPoolPage({agentPoolRawId, agentPoolQueryRef, onUpdate}: Props) {
  const {agentPool, defaultPool}: AgentPoolPagePoolQuery$data = usePreloadedQuery(
    agentPoolQuery,
    agentPoolQueryRef,
  )
  const pools = useAgentPools()

  useUpdateLastPage(true, null, LAST_AGENTS_PAGE_KEY, getAgentsOverviewHref())

  useDocumentTitle(`Agent Pool ${agentPool?.name ? `(${agentPool.name})` : ''} — TeamCity`)
  const tabParam = useQueryParam('tab')
  const tab = tabParam != null ? toTabId(tabParam) : OverviewAgentPoolTabNamesEnum.AGENTS
  const location = useLocation()
  const navigate = useNavigate()
  const onSelectTab = React.useCallback(
    (selectedTab: string) => {
      navigate(
        getHrefWithQueryParams(location, null, () => ({
          tab: selectedTab,
        })),
      )
    },
    [location, navigate],
  )

  const handleUpdate = React.useCallback(() => {
    if (onUpdate != null) {
      onUpdate()
    }
  }, [onUpdate])
  const handleRemove = React.useCallback(() => {
    handleUpdate()
    navigate(getAgentsOverviewHref())
  }, [navigate, handleUpdate])

  const agentsTree = useAppSelector(state =>
    getAgentPoolsTree(state, {
      pools,
    }),
  )

  const renderProjectsTabTitle = React.useCallback(
    (isSelected: boolean) => (
      <ProjectsTabTitle
        agentPoolId={agentPoolRawId}
        agentPool={agentPool}
        isSelected={isSelected}
      />
    ),
    [agentPoolRawId, agentPool],
  )

  const agentPoolTree = agentsTree.find(({id}) => id === agentPoolRawId)

  const isProjectPool = agentPool?.__typename === PROJECT_AGENT_POOL_TYPENAME
  const isDefaultPool = agentPool?.rawId === STRING_DEFAULT_AGENT_POOL_ID

  const canManageAgents = agentPool?.permissions?.manageAgents === true && !isProjectPool
  const canRemoveAgents =
    !isDefaultPool && canManageAgents && defaultPool?.permissions?.manageAgents === true
  const canAssignProjects = agentPool?.permissions?.manageProjects === true && !isProjectPool
  const canManageProperties =
    agentPool?.permissions?.manage === true && !isProjectPool && !isDefaultPool

  return useMemo(
    () =>
      agentPool == null ||
      agentPoolTree == null ||
      agentPoolTree.type !== AgentTreeItemType.POOL ? null : (
        <div className={styles.page}>
          <div className={styles.heading}>
            <div>
              <H1 className={styles.header}>
                {agentPool.name} <span className={styles.titleExplanation}>{'agent pool'}</span>
                <AgentPoolFavoriteStar className={styles.star} id={Number(agentPool.rawId)} />
              </H1>
              {isProjectPool ? (
                <div className={styles.description}>
                  {
                    'This is an automatically created agent pool. It’s not possible to edit its’ properties and agents or projects associations.'
                  }
                </div>
              ) : null}
            </div>
            {canManageProperties ? (
              internalProps['teamcity.ui.newAgentPoolManagement'] !== false ? (
                <EditAgentPoolButton
                  agentPool={agentPool}
                  className={styles.editButton}
                  onUpdate={handleUpdate}
                  onRemove={handleRemove}
                />
              ) : (
                <EditInClassicUILink className={styles.editButton} agentPoolId={agentPoolRawId}>
                  {'Edit pool...'}
                </EditInClassicUILink>
              )
            ) : null}
          </div>
          <Plugin placeId={PlaceIdList.SAKURA_BEFORE_CONTENT} />
          <Tabs selected={tab} onSelect={onSelectTab}>
            <Tab
              id={TabNames.AGENTS}
              title={renderTitle(
                'Agents',
                agentPoolTree.count > 0 ? String(agentPoolTree.count) : '0',
              )}
              titleProps={{'aria-label': 'Agents'}}
              href={`${getAgentPoolHref(agentPoolRawId)}?tab=${String(TabNames.AGENTS)}`}
            >
              <AgentsTab
                agentPoolId={agentPoolRawId}
                agentPoolTree={agentPoolTree}
                maxAgentsNumber={agentPool.maxAgentsNumber}
                canManageAgents={canManageAgents}
                canRemoveAgents={canRemoveAgents}
              />
            </Tab>
            <Tab
              id={TabNames.PROJECTS}
              title={renderProjectsTabTitle}
              titleProps={{'aria-label': 'Projects'}}
              href={`${getAgentPoolHref(agentPoolRawId)}?tab=${String(TabNames.PROJECTS)}`}
            >
              <ProjectsTab agentPoolId={agentPoolRawId} canAssignProjects={canAssignProjects} />
            </Tab>
          </Tabs>
        </div>
      ),
    [
      agentPool,
      agentPoolRawId,
      agentPoolTree,
      canAssignProjects,
      canManageAgents,
      canManageProperties,
      canRemoveAgents,
      handleRemove,
      handleUpdate,
      isProjectPool,
      onSelectTab,
      renderProjectsTabTitle,
      tab,
    ],
  )
}

type ContainerProps = {
  agentPoolId: string
  agentPoolQueryRef: PreloadedQuery<AgentPoolPagePoolQuery>
}

function AgentPoolPageContainer(props: ContainerProps) {
  const stringAgentPoolId = props.agentPoolId
  const agentPoolRawId = toAgentPoolId(stringAgentPoolId)

  const [agentPoolQueryRef, loadAgentPoolPropsQuery, disposeAgentPoolPropsQuery] = useQueryLoader(
    agentPoolQuery,
    props.agentPoolQueryRef,
  )

  const loadAgentPool = React.useCallback(
    () =>
      loadAgentPoolPropsQuery(
        {
          agentPoolId: stringAgentPoolId,
          isDefault: stringAgentPoolId === STRING_DEFAULT_AGENT_POOL_ID,
        },
        {
          fetchPolicy: 'store-and-network',
        },
      ),
    [loadAgentPoolPropsQuery, stringAgentPoolId],
  )
  React.useEffect(
    () => subscribeOnOverallEvents(OVERALL_AGENT_POOL_EVENT_TOPICS, loadAgentPool),
    [loadAgentPool],
  )
  React.useEffect(() => disposeAgentPoolPropsQuery, [disposeAgentPoolPropsQuery])

  return (
    <AvailabilityErrorPage
      getErrorMessage={error =>
        getAvailabilityErrorMessage({
          error,
          agentPoolId: agentPoolRawId,
        })
      }
    >
      <ErrorBoundary
        fallbackRender={({error, resetErrorBoundary}) =>
          error != null ? (
            <ErrorPage
              error={
                getAvailabilityErrorMessage({
                  error,
                  agentPoolId: agentPoolRawId,
                }) || error
              }
              notFound={error instanceof HTTPError && error.statusCode === HTTPCodesEnum.NOT_FOUND}
              onRetry={resetErrorBoundary}
              referrer={null}
            />
          ) : null
        }
      >
        {agentPoolQueryRef != null ? (
          <React.Suspense fallback={loader}>
            <AgentPoolPage
              agentPoolRawId={agentPoolRawId}
              key={stringAgentPoolId}
              agentPoolQueryRef={agentPoolQueryRef}
              onUpdate={loadAgentPool}
            />
          </React.Suspense>
        ) : (
          loader
        )}
      </ErrorBoundary>
    </AvailabilityErrorPage>
  )
}

type Params = {
  agentPoolId: string
}

type AgentPoolPageQueries = AgentsPagesQueries & {
  agentPool: AgentPoolPagePoolQuery
}

export default function AgentPoolPageOuterContainer({
  queries,
}: EntryPointProps<AgentPoolPageQueries, {}, {}, {}>) {
  const {agentPoolId = ''} = useParams<Params>()
  return (
    <AgentPagePoolsSubscriber agentTypesQueryRef={queries.agentTypes}>
      <AgentPoolPageContainer
        agentPoolId={agentPoolId}
        key={agentPoolId}
        agentPoolQueryRef={queries.agentPool}
      />
    </AgentPagePoolsSubscriber>
  )
}

export type AgentPoolPageOuterContainerType = typeof AgentPoolPageOuterContainer
